/**
 * Microcourse Studio — Zod Schemas
 * 用于验证 LLM 输出、API 输入
 */

import { z } from 'zod'

// ─── Element Schemas ───────────────────────────────────────────────

const elementStyleSchema = z.object({
  align: z.enum(['left', 'center', 'right']).optional(),
  fontSize: z.string().optional(),
  textColor: z.string().optional(),
  bgColor: z.string().optional(),
  padding: z.string().optional(),
  borderRadius: z.string().optional(),
  marginTop: z.string().optional(),
  marginBottom: z.string().optional(),
}).passthrough().optional()

const baseElementSchema = z.object({
  id: z.string(),
  style: elementStyleSchema,
})

export const headingElementSchema = baseElementSchema.extend({
  type: z.literal('heading'),
  content: z.string(),
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
})

export const paragraphElementSchema = baseElementSchema.extend({
  type: z.literal('paragraph'),
  content: z.string(),
})

export const quoteElementSchema = baseElementSchema.extend({
  type: z.literal('quote'),
  content: z.string(),
  citation: z.string().optional(),
})

export const listElementSchema = baseElementSchema.extend({
  type: z.literal('list'),
  content: z.string(),
  ordered: z.boolean().optional(),
})

export const codeElementSchema = baseElementSchema.extend({
  type: z.literal('code'),
  content: z.string(),
  language: z.string().optional(),
})

export const mathElementSchema = baseElementSchema.extend({
  type: z.literal('math'),
  content: z.string(),
  displayMode: z.boolean().optional(),
})

export const imageElementSchema = baseElementSchema.extend({
  type: z.literal('image'),
  content: z.string(),
  caption: z.string().optional(),
  alt: z.string().optional(),
  width: z.string().optional(),
})

export const videoElementSchema = baseElementSchema.extend({
  type: z.literal('video'),
  content: z.string(),
  caption: z.string().optional(),
  poster: z.string().optional(),
})

export const audioElementSchema = baseElementSchema.extend({
  type: z.literal('audio'),
  content: z.string(),
  caption: z.string().optional(),
})

export const iframeElementSchema = baseElementSchema.extend({
  type: z.literal('iframe'),
  content: z.string(),
  title: z.string().optional(),
  height: z.number().optional(),
})

export const tableElementSchema = baseElementSchema.extend({
  type: z.literal('table'),
  content: z.string(),
})

export const dividerElementSchema = baseElementSchema.extend({
  type: z.literal('divider'),
  content: z.string().optional(),
})

export const spacerElementSchema = baseElementSchema.extend({
  type: z.literal('spacer'),
  content: z.string().optional(),
  height: z.number().optional(),
})

export const geometryElementSchema = baseElementSchema.extend({
  type: z.literal('geometry'),
  content: z.string(),
  title: z.string().optional(),
  height: z.number().optional(),
  autoStep: z.boolean().optional(),
})

export const svgExplainElementSchema = baseElementSchema.extend({
  type: z.literal('svgExplain'),
  content: z.string(),
  title: z.string().optional(),
  height: z.number().optional(),
  autoStep: z.boolean().optional(),
})

export const jsxgraphExplainElementSchema = baseElementSchema.extend({
  type: z.literal('jsxgraphExplain'),
  content: z.string(),
  title: z.string().optional(),
  height: z.number().optional(),
})

export const geogebraExplainElementSchema = baseElementSchema.extend({
  type: z.literal('geogebraExplain'),
  content: z.string(),
  title: z.string().optional(),
  height: z.number().optional(),
})

export const excalidrawElementSchema = baseElementSchema.extend({
  type: z.literal('excalidraw'),
  content: z.string(),
  title: z.string().optional(),
  height: z.number().optional(),
  viewMode: z.boolean().optional(),
  renderedSvg: z.string().optional(),
})

// ─── Quiz Schema ───────────────────────────────────────────────────

const quizOptionSchema = z.object({
  label: z.string(),
  content: z.string(),
  images: z.array(z.string()).optional(),
})

/** 新结构（与题库 questions 对齐） */
const quizContentSchema = z.object({
  type: z.enum(['choice', 'multi_choice', 'fill_blank', 'true_false']),
  stem: z.string(),
  images: z.array(z.string()).optional(),
  options: z.array(quizOptionSchema).optional(),
  answer: z.object({ value: z.string() }),
  analysis: z.string().optional(),
})

/** 存量旧结构（兼容已有课件数据，运行时由 normalizeQuizContent 转换） */
const legacyQuizContentSchema = z.object({
  quizType: z.enum(['single_choice', 'multiple_choice', 'fill_blank', 'true_false']),
  question: z.string(),
  imageUrl: z.string().optional(),
  options: z.array(z.object({ id: z.string(), text: z.string(), imageUrl: z.string().optional() })).optional(),
  correctOptionId: z.string().optional(),
  correctOptionIds: z.array(z.string()).optional(),
  correctAnswer: z.boolean().optional(),
  fillText: z.string().optional(),
  answer: z.union([z.string(), z.array(z.string())]).optional(),
  explanation: z.string().optional(),
})

export const quizElementSchema = baseElementSchema.extend({
  type: z.literal('quiz'),
  content: z.union([quizContentSchema, legacyQuizContentSchema]),
})

// ─── Element Union Schema ──────────────────────────────────────────

export const microElementSchema = z.discriminatedUnion('type', [
  headingElementSchema,
  paragraphElementSchema,
  quoteElementSchema,
  listElementSchema,
  codeElementSchema,
  mathElementSchema,
  imageElementSchema,
  videoElementSchema,
  audioElementSchema,
  iframeElementSchema,
  tableElementSchema,
  dividerElementSchema,
  spacerElementSchema,
  geometryElementSchema,
  svgExplainElementSchema,
  jsxgraphExplainElementSchema,
  geogebraExplainElementSchema,
  quizElementSchema,
  excalidrawElementSchema,
])

// ─── Segment Schema ────────────────────────────────────────────────

export const narrationSegmentSchema = z.object({
  ttsText: z.string(),
  lines: z.array(z.string()).default([]),
  audioUrl: z.string().optional(),
  durationMs: z.number().optional(),
})

// ─── Slide Schema ──────────────────────────────────────────────────

const slideBackgroundSchema = z.object({
  type: z.enum(['color', 'gradient', 'image']),
  value: z.string(),
  opacity: z.number().optional(),
})

export const microSlideSchema = z.object({
  id: z.string(),
  title: z.string(),
  chapterId: z.string().optional(),
  order: z.number(),
  background: slideBackgroundSchema.optional(),
  transition: z.string().optional(),
  notes: z.string().optional(),
  elements: z.array(microElementSchema),
  segments: z.array(narrationSegmentSchema).default([]),
})

// ─── Chapter Schema ────────────────────────────────────────────────

export const microChapterSchema = z.object({
  id: z.string(),
  name: z.string(),
  collapsed: z.boolean().optional(),
})

// ─── Project Schema ────────────────────────────────────────────────

export const projectColorsSchema = z.object({
  primary: z.string(),
  accent: z.string(),
  bg: z.string(),
})

export const microcourseProjectSchema = z.object({
  id: z.string(),
  title: z.string(),
  theme: z.string(),
  customColors: projectColorsSchema,
  fontFamily: z.string(),
  transition: z.string(),
  fontScale: z.number(),
  chapters: z.array(microChapterSchema),
  slides: z.array(microSlideSchema),
})

// ─── Lesson Message Schema ─────────────────────────────────────────

export const microLessonMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  ref_slide_index: z.number().optional(),
  ref_element_id: z.string().optional(),
  action: z.enum([
    'generate', 'regenerate_all', 'patch_slide',
    'modify_element', 'add_slide', 'remove_slide',
  ]).optional(),
  patch: z.record(z.unknown()).optional(),
  timestamp: z.string(),
})

// ─── API Input Schemas ─────────────────────────────────────────────

export const generateLessonInput = z.object({
  topic: z.string().min(1).max(200),
  stage: z.string().optional(),
  grade: z.string().optional(),
  subject: z.string().optional(),
  materials: z.string().optional(),
})

export const modifyLessonInput = z.object({
  lessonId: z.string().uuid(),
  message: z.string().min(1),
  refSlideIndex: z.number().optional(),
  refElementId: z.string().optional(),
})

export const generateFromDiagnosisInput = z.object({
  sessionId: z.string().uuid(),
  kpCode: z.string().min(1),
  kpName: z.string().optional(),
})

export const updateLessonSettingsInput = z.object({
  lessonId: z.string().uuid(),
  theme: z.string().optional(),
  fontFamily: z.string().optional(),
  transition: z.string().optional(),
  fontScale: z.number().min(0.5).max(2.0).optional(),
  customColors: z.object({
    primary: z.string(),
    accent: z.string(),
    bg: z.string(),
  }).partial().optional(),
})

// ─── Inferred Types ────────────────────────────────────────────────

export type MicrocourseProjectInput = z.infer<typeof microcourseProjectSchema>
export type MicroSlideInput = z.infer<typeof microSlideSchema>
export type MicroElementInput = z.infer<typeof microElementSchema>
export type NarrationSegmentInput = z.infer<typeof narrationSegmentSchema>
